import React, { useState, useEffect } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Chip from "@mui/material/Chip";
import { faRedo, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "./OTFilterBar.css";

const OTSettingBar = ({ applySearch, applyFilters, resetFilters, activeSearchTerms, removeSearchTerm }) => {
  const [searchInput, setSearchInput] = useState("");
  const [platforms, setPlatforms] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    const savedFilterData = localStorage.getItem("filterState");
    if (savedFilterData) {
      const { platforms, startDate, endDate } = JSON.parse(savedFilterData);
      setPlatforms(platforms || []);
      setStartDate(startDate ? new Date(startDate) : null);
      setEndDate(endDate ? new Date(endDate) : null);
    }
  }, []);

  // // Save filters state to localStorage whenever they change
  // useEffect(() => {
  //   const filterData = { platforms, startDate, endDate };
  //   localStorage.setItem("filterState", JSON.stringify(filterData));
  // }, [platforms, startDate, endDate]);

  const handleApplyFilters = () => {
    if (startDate && endDate && startDate > endDate) {
      alert("Start Date cannot be later than End Date.");
      return;
    }

    applyFilters({
      platforms,
      startDate: startDate ? startDate.getTime() : null,
      endDate: endDate ? endDate.getTime() : null,
    });
  };

  const handleSearchButton = () => {
    if (!searchInput.trim()) return;
    
    applySearch(searchInput.trim());
    setSearchInput(""); // Clear input after adding search term
  };

  const handleResetFilters = () => {
    setPlatforms([]);
    setStartDate(null);
    setEndDate(null);
    resetFilters();
  };

  // Function to handle platform selection changes
  const handlePlatformChange = (event) => {
    setPlatforms(event.target.value);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearchButton();
    }
  };

  return (
    <div className="OT-setting-bar">
      {/* Search Bar */}
      <div className="OT-search-bar">
        <div className="search-input-container">
          <input
            type="text"
            id="search"
            placeholder={activeSearchTerms.length > 0 ? "Add another search term..." : "Search"}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={handleKeyPress}
          />
          <button
            id="OT-search-button"
            onClick={handleSearchButton}
            disabled={!searchInput.trim()}
            title="Add search term"
          >
            <FontAwesomeIcon icon={faPlus} /> Add
          </button>
        </div>
        
        {/* Active search terms */}
        {activeSearchTerms.length > 0 && (
          <div className="active-search-terms">
            {activeSearchTerms.map((term, index) => (
              <Chip
                key={`${term}-${index}`}
                label={term}
                onDelete={() => removeSearchTerm(term)}
                className="search-term-chip"
                color="primary"
              />
            ))}
          </div>
        )}
      </div>

      {/* Filter Bar */}
      <div className="OT-filter-bar">
        {/* Platform Selection */}
        <div className="OT-filter-options">
          <FormControl style={{ minWidth: 200, marginRight: 10 }}>
            <InputLabel id="platform-label">Select Platform(s)</InputLabel>
            <Select
              labelId="platform-label"
              id="platform-select"
              multiple
              value={platforms}
              onChange={handlePlatformChange}
              renderValue={(selected) => (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} />
                  ))}
                </div>
              )}
            >
              <MenuItem value="facebook">
                <img
                  src="/icons/facebook.png"
                  alt="Facebook"
                  style={{ width: 20, height: 20, marginRight: 10 }}
                />
                Facebook
              </MenuItem>

              <MenuItem value="twitter">
                <img
                  src="/icons/twitter.png"
                  alt="Twitter"
                  style={{ width: 20, height: 20, marginRight: 10 }}
                />
                Twitter
              </MenuItem>

              <MenuItem value="instagram">
                <img
                  src="/icons/instagram.png"
                  alt="Instagram"
                  style={{ width: 20, height: 20, marginRight: 10 }}
                />
                Instagram
              </MenuItem>

              <MenuItem value="tiktok">
                <img
                  src="/icons/tiktok.png"
                  alt="TikTok"
                  style={{ width: 20, height: 20, marginRight: 10 }}
                />
                TikTok
              </MenuItem>
            </Select>
          </FormControl>
        </div>

        {/* Date Filters */}
        <div className="OT-date-filters">
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <div className="OT-date-picker">
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                renderInput={(params) => <TextField {...params} />}
              />
            </div>
            <div className="OT-date-picker">
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                renderInput={(params) => <TextField {...params} />}
              />
            </div>
          </LocalizationProvider>
        </div>

        {/* Apply Filters Button */}
        <div className="OT-apply-button">
          <button id="OT-apply-button" onClick={handleApplyFilters}>
            Apply
          </button>
          <button variant="outlined" color="default" onClick={handleResetFilters} style={{ marginLeft: 10 }}>
            <FontAwesomeIcon icon={faRedo} /> Reset
          </button>
        </div>
      </div>
    </div>
  );
};

export default OTSettingBar;